/**
 * @file finance-return-report.tsx
 * @module app/modules/finance-reports/finance-return-report
 * @description Displays the finance return report with filters, totals, and paginated return records.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { Card, Col, FormLabel, Row, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  FinanceReturnReportPermission,
  PAGE_LIMIT,
  Piece,
  ReturnProductReports,
  Download,
  SellersSalesReport,
} from "../../../utils/constants";
import APICallService from "../../../api/apiCallService";
import { seller } from "../../../api/apiEndPoints";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { useAuth } from "../auth";
import clsx from "clsx";
import { CustomSelect } from "../../custom/Select/CustomSelect";
import { useWarehouse } from "../../context/WarehouseContext";
import React from "react";
import ImageWithFallback from "../_common/components/image-with-fallback";

const FinanceReturnReport = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [details, setDetails] = useState<any>();
  const [endDate, setEndDate] = useState<any>(new Date());
  const [startDate, setStartDate] = useState<any>(new Date());
  const [fetchLoading, setFetchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [search, setSearch] = useState<string>("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [paymentDetails, setPaymentDetails] = useState<any>([]);
  const [productLoading, setProductLoading] = useState(false);
  const [searchProduct, setSearchProduct] = useState("");
  const [totalProducts, setTotalProducts] = useState(0);
  const [selectedBrand, setSelectedBrand] = useState<any>();
  const [selectedDistrict, setSelectedDistrict] = useState<any>();
  const [selectedProduct, setSelectedProduct] = useState<any>();
  const [productsList, setProductsList] = useState<any>([]);
  const [brands, setBrands] = useState<any>([]);
  const [district, setDistricts] = useState<any>([]);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [driversList, setDriversList] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const exportRef = React.useRef<number>(0);
  const isAllowedToDownload = Method.hasPermission(
    FinanceReturnReportPermission,
    Download,
    currentUser
  );

  useEffect(() => {
    (async () => {
      if (!Method.hasModulePermission(SellersSalesReport, currentUser)) {
        return window.history.back();
      }
      if (!selectedWarehouse?.value) return;
      setFetchLoading(true);
      setSelectedBrand(null);
      setSelectedProduct(null);
      setSelectedDistrict(null);
      setSelectedDriver(null);
      setDriversList([]);
      await fetchOrder(
        startDate,
        endDate,
        selectedBrand ? selectedBrand?._id : null,
        selectedProduct ? selectedProduct?.value : null
      );
      setFetchLoading(false);
    })();
  }, [selectedWarehouse?.value]);

  const fetchOrder = async (
    startDate: string,
    endDate: string,
    brands?: any,
    product?: any,
    district?: any,
    driver?: any
  ) => {
    if (!selectedWarehouse?.value) return false;

    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      utcOffset: -180,
    };
    if (brands) {
      params = {
        ...params,
        ["brands[0]"]: brands,
      };
    }
    if (district) {
      params = {
        ...params,
        ["districts[0]"]: district,
      };
    }
    if (product) {
      params = {
        ...params,
        ["variants[0]"]: product,
      };
    }
    if (driver) {
      params.driver = driver;
    }
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      seller.returnReport,
      params,
      "",
      "",
      "",
      "",
      FinanceReturnReportPermission,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      setPaymentData(response);
      getUniqueProducts(response);
      getUniqueBrands(response);
      getUniqueDistricts(response);
      if (!driver) {
        getUniqueDrivers(response);
      }
    }
  };

  const getUniqueProducts = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant;
      const variantInfo = {
        title: variant?.title,
        id: variant?._id,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant)
    );
    setProductsList(uniqueVariantsArray);
  };

  const getUniqueBrands = (data: any) => {
    const uniqueVariants = new Set();
    data.forEach((item: any) => {
      const variant = item?.variant?.product?.brand;
      const variantInfo = {
        title: variant?.title,
        id: variant?.reference,
        media: variant?.media,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant)
    );
    setBrands(uniqueVariantsArray);
  };

  const getUniqueDistricts = (data: any) => {
    const uniqueVariants = new Set();
    data?.forEach((item: any) => {
      const variant = item?.district;
      const variantInfo = {
        title: variant?.name,
        id: variant?.id,
      };
      uniqueVariants?.add(JSON.stringify(variantInfo));
    });
    const uniqueVariantsArray = Array?.from(uniqueVariants)?.map(
      (variant: any) => JSON.parse(variant)
    );
    setDistricts(uniqueVariantsArray);
  };

  const getUniqueDrivers = (data: any) => {
    const uniqueDrivers = new Map();
    data?.forEach((item: any) => {
      const driverId = item?.driverId;
      const driverName = item?.driverName;
      if (driverId && driverName) {
        uniqueDrivers.set(String(driverId), {
          id: String(driverId),
          name: driverName,
        });
      }
    });
    const uniqueDriversArray = Array.from(uniqueDrivers.values()).sort(
      (a: any, b: any) => a.name.localeCompare(b.name)
    );
    setDriversList(uniqueDriversArray);
  };

  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      setFetchLoading(true);
      setTotalRecords(0);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        selectedProduct ? selectedProduct?.value : null,
        selectedDistrict ? selectedDistrict?.value : null,
        selectedDriver ? selectedDriver?.value : null
      );
      setFetchLoading(false);
    }
  };

  const handleProductSelect = async (event: any) => {
    if (event) {
      setTotalRecords(0);
      setPaymentData([]);
      setFetchLoading(true);
      setSelectedProduct(event);
      setPage(1);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        event.value,
        selectedDistrict ? selectedDistrict?.id : null,
        selectedDriver ? selectedDriver?.value : null
      );
      setFetchLoading(false);
    } else {
      setTotalRecords(0);
      setPaymentData([]);
      setFetchLoading(true);
      setSelectedProduct(null);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        null,
        selectedDistrict ? selectedDistrict?.id : null,
        selectedDriver ? selectedDriver?.value : null
      );
      setFetchLoading(false);
    }
  };

  const handleDriverSelect = async (event: any) => {
    if (event) {
      setTotalRecords(0);
      setPaymentData([]);
      setFetchLoading(true);
      setSelectedDriver(event);
      setPage(1);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        selectedProduct ? selectedProduct?.value : null,
        selectedDistrict ? selectedDistrict?.id : null,
        event.value
      );
      setFetchLoading(false);
    } else {
      setTotalRecords(0);
      setPaymentData([]);
      setFetchLoading(true);
      setSelectedDriver(null);
      await fetchOrder(
        Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
        Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
        selectedBrand ? selectedBrand?.id : null,
        selectedProduct ? selectedProduct?.value : null,
        selectedDistrict ? selectedDistrict?.id : null,
        null
      );
      setFetchLoading(false);
    }
  };

  const onMenuClose = async () => {
    setSearch("");
  };
  const onMenuOpen = async () => {
    setPage(1);
  };

  // --- Summary Calculations ---
  const totalDeliveredInclVAT = paymentData.reduce(
    (acc: number, val: any) => acc + (val?.totalAmount || 0),
    0
  );
  const totalDeliveredExclVAT = paymentData.reduce(
    (acc: number, val: any) => acc + (val?.totalTaxAmount || 0),
    0
  );
  const totalDeliveredVAT = paymentData.reduce(
    (acc: number, val: any) => acc + (val?.tax || 0),
    0
  );

  const totalReturnedInclVAT = paymentData.reduce((acc: number, val: any) => {
    const returnedQty = val?.totalReturnedQuantities || 0;
    return acc + returnedQty * (val?.averagePrice || 0);
  }, 0);
  const totalReturnedExclVAT = paymentData.reduce((acc: number, val: any) => {
    const returnedQty = val?.totalReturnedQuantities || 0;
    return acc + returnedQty * (val?.averageTaxPrice || 0);
  }, 0);
  const totalReturnedVAT = paymentData.reduce((acc: number, val: any) => {
    const returnedQty = val?.totalReturnedQuantities || 0;
    return acc + returnedQty * (val?.taxPerUnit || 0);
  }, 0);

  const netInclVAT = totalDeliveredInclVAT - totalReturnedInclVAT;
  const netExclVAT = totalDeliveredExclVAT - totalReturnedExclVAT;
  const netVAT = totalDeliveredVAT - totalReturnedVAT;

  const exportHeaders = [
    "Product Name",
    "Reference Key",
    "Customer Name",
    "Unit Price (Incl. VAT)",
    "Unit Price (Excl. VAT)",
    "VAT / Unit",
    "Outward Quantity",
    "Returned Quantity",
    "Returned Total (Incl. VAT)",
    "Returned Total (Excl. VAT)",
    "Returned VAT Amount",
  ];

  const rowMapper = (val: any) => {
    const totalQty = val?.totalQuantities || 0;
    const returnedQty = val?.totalReturnedQuantities || 0;

    const returnedTotalIncl = returnedQty * (val?.averagePrice || 0);
    const returnedTotalExcl = returnedQty * (val?.averageTaxPrice || 0);
    const returnedVAT = returnedQty * (val?.taxPerUnit || 0);

    return [
      val?.variant?.title || "N/A",
      val?.refKey || "N/A",
      val?.customerName || "N/A",
      val?.averagePrice || 0,
      val?.averageTaxPrice || 0,
      val?.taxPerUnit || 0,
      totalQty,
      returnedQty,
      returnedTotalIncl,
      returnedTotalExcl,
      returnedVAT,
    ];
  };

  const exportParams = {
    fromDate: startDate
      ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
      : "",
    toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
    utcOffset: -180,
    ...(selectedBrand && { "brands[0]": selectedBrand.id }),
    ...(selectedProduct && { "variants[0]": selectedProduct.value }),
    ...(selectedDistrict && { "districts[0]": selectedDistrict.value }),
    ...(selectedDriver && { driver: selectedDriver.value }),
  };

  const handleExportExcel = async () => {
    const runId = ++exportRef.current;
    const toastId = toast.loading("Fetching all records...");
    const EXPORT_LIMIT = 10;
    const sanitizedParams: any = { ...exportParams, download: true };
    if (selectedWarehouse?.value)
      sanitizedParams.warehouse = selectedWarehouse.value;

    try {
      setIsExporting(true);
      if (runId !== exportRef.current) {
        toast.dismiss(toastId);
        return;
      }

      const apiService = new APICallService(
        seller.returnReport,
        sanitizedParams,
        "",
        "",
        "",
        "",
        FinanceReturnReportPermission,
        selectedWarehouse?.value
      );
      const response = await apiService.callAPI();

      if (runId !== exportRef.current) return;
      if (!response) throw new Error("Connection lost or API error.");

      const data = Array.isArray(response) ? response : response?.records || [];

      if (data.length === 0) {
        if (toastId) {
          toast.update(toastId, {
            render: "No records found to export",
            type: "warning",
            isLoading: false,
            autoClose: 3000,
            closeButton: true,
          });
        } else {
          toast.warning("No records found to export");
        }
        return;
      }

      const workbook = XLSX.utils.book_new();
      const worksheetData: any[] = [exportHeaders];

      toast.update(toastId, {
        render: `Mapping ${data.length} records...`,
        type: "info",
        isLoading: true,
      });
      worksheetData.push(...data.map(rowMapper));

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet["!cols"] = [
        { width: 35 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
        { width: 15 },
        { width: 15 },
        { width: 20 },
        { width: 20 },
        { width: 15 },
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Return Report");
      XLSX.writeFile(
        workbook,
        `Finance-Return-Report-${new Date().toISOString().split("T")[0]}.xlsx`
      );
      if (toastId) {
        toast.update(toastId, {
          render: "Export completed successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
          closeButton: true,
          progress: undefined,
        });
      } else {
        toast.success("Export completed successfully!");
      }
    } catch (error) {
      if (runId === exportRef.current) {
        if (toastId) {
          toast.update(toastId, {
            render: "Export failed: " + (error as Error).message,
            type: "error",
            isLoading: false,
            autoClose: 4000,
            closeButton: true,
          });
        } else {
          toast.error("Export failed: " + (error as Error).message);
        }
      }
    } finally {
      if (runId === exportRef.current) setIsExporting(false);
    }
  };

  return (
    <div className="p-5">
      {/* Title & Reload Row */}
      <Row className="g-4 align-items-center mb-4">
        <Col xs={12} md="auto">
          <div className="d-flex align-items-center">
            <h1 className="fs-22 fw-bolder mb-0">Return Report</h1>
            <button
              type="button"
              className="btn btn-icon btn-sm btn-light-primary ms-3"
              title="Reload report data"
              aria-label="Reload report data"
              disabled={fetchLoading}
              onClick={() => {
                void fetchOrder(
                  Method.convertDateToFormat(startDate, "YYYY-MM-DD"),
                  Method.convertDateToFormat(endDate, "YYYY-MM-DD"),
                  selectedBrand ? selectedBrand?.id : null,
                  selectedProduct ? selectedProduct?.value : null,
                  selectedDistrict ? selectedDistrict?.value : null,
                  selectedDriver ? selectedDriver?.value : null
                );
              }}
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            </button>
            {fetchLoading && (
              <span
                className="d-inline-flex align-items-center ms-4 fs-14 fw-semibold text-primary"
                role="status"
                aria-live="polite"
              >
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
                Loading report data...
              </span>
            )}
          </div>
        </Col>
      </Row>

      {/* Filters Row */}
      <Row className="g-4 gx-lg-4 gx-xl-5 align-items-end mt-1 mb-4">
        <Col xs={12} sm={6} lg="auto" style={{ width: "300px" }}>
          <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
            Duration
          </FormLabel>
          <div className="w-100">
            <CustomDatePicker
              className="form-control bg-white min-h-60px fs-16 fw-bold text-dark w-100 cursor-pointer"
              onChange={handleChange}
              selectsRange={true}
              startDate={startDate}
              endDate={endDate}
              dateFormat="dd/MM/yyyy"
              showFullMonthYearPicker={true}
              maxDate={new Date()}
              disabled={fetchLoading}
              inputTextBG="bg-white"
            />
          </div>
        </Col>

        <Col xs={12} sm={6} lg="auto" style={{ width: "300px" }}>
          <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
            Filter by Product
          </FormLabel>
          <div className="w-100">
            <CustomSelect
              backgroundColor="#ffff"
              isClearable={true}
              minWidth="100%"
              minHeight="60px"
              controlFontSize="15px"
              optionFontSize="14px"
              placeholder="Select Product"
              options={
                productsList && productsList?.length
                  ? productsList?.map((val: any) => ({
                      label: (
                        <div className="d-flex align-items-center">
                          <div className="symbol symbol-30px border me-3">
                            <ImageWithFallback
                              src={val?.media[0]?.url}
                              className="object-fit-contain"
                              alt=""
                              noDefaultSize
                            />
                          </div>
                          <span className="fs-16 fw-600 text-black mb-0">
                            {val?.title}
                          </span>
                        </div>
                      ),
                      value: val?.id,
                      id: val?.id,
                      variant: val,
                      title: val?.title,
                    }))
                  : []
              }
              value={selectedProduct}
              onChange={(event: any) => handleProductSelect(event)}
              onMenuClose={() => onMenuClose()}
              onMenuOpen={() => onMenuOpen()}
              disabled={productLoading || fetchLoading}
              isDisabled={productLoading || fetchLoading}
              isLoading={fetchLoading}
              hideSelectedOptions={false}
            />
          </div>
        </Col>

        <Col xs={12} sm={6} lg="auto" style={{ width: "300px" }}>
          <FormLabel className="d-block fs-16 fw-500 mb-2 text-nowrap">
            Filter by Driver
          </FormLabel>
          <div className="w-100">
            <CustomSelect
              backgroundColor="#ffff"
              isClearable={true}
              minWidth="100%"
              minHeight="60px"
              controlFontSize="15px"
              optionFontSize="14px"
              placeholder="Select Driver"
              options={
                driversList && driversList.length
                  ? driversList.map((driver: any) => ({
                      label: driver.name,
                      value: driver.id,
                    }))
                  : []
              }
              value={selectedDriver}
              onChange={(event: any) => handleDriverSelect(event)}
              disabled={fetchLoading || !driversList.length}
              isDisabled={fetchLoading || !driversList.length}
              isLoading={fetchLoading}
              hideSelectedOptions={false}
            />
          </div>
        </Col>

        <Col xs={12} lg="auto" className="d-flex align-items-end gap-3 ms-lg-auto">
          {isAllowedToDownload && (
            <button
              className="uw-btn uw-btn-primary btn btn-primary fs-15 fw-bold px-6 min-h-60px d-inline-flex align-items-center"
              onClick={handleExportExcel}
              disabled={fetchLoading || isExporting}
              data-analytics-event="finance_reports_finance_return_report_download_clicked"
            >
              <i className="bi bi-file-earmark-excel fs-4 me-2"></i>
              {isExporting ? "Exporting..." : "Download Report"}
            </button>
          )}
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="mt-5 g-5">
        <Col xl={4} lg={6}>
          <Card className="border border-dashed bg-opacity-25 shadow-sm h-100 yellow-card">
            <Card.Header
              className="min-h-50px px-6 py-3 border-bottom"
              style={{ borderColor: "rgba(255, 204, 0, 0.25)" }}
            >
              <div className="card-title m-0 d-flex align-items-center">
                <span
                  className="fs-15 fw-bolder text-uppercase"
                  style={{ color: "#FFCC00" }}
                >
                  Total Returned Summary
                </span>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-between flex-wrap align-items-center p-6 gap-3">
              <div className="d-flex flex-column flex-grow-1">
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  INCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark">
                  TSh {Method.getGeneralizedAmount(totalReturnedInclVAT)}
                </span>
              </div>
              <div
                className="d-flex flex-column flex-grow-1 border-start ps-5"
                style={{ borderColor: "rgba(255, 204, 0, 0.25)" }}
              >
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  EXCL VAT
                </span>
                <span className="fs-22 fw-bolder text-dark">
                  TSh {Method.getGeneralizedAmount(totalReturnedExclVAT)}
                </span>
              </div>
              <div
                className="d-flex flex-column flex-grow-1 border-start ps-5"
                style={{ borderColor: "rgba(255, 204, 0, 0.25)" }}
              >
                <span className="fs-13 fw-bold text-gray-500 mb-1">
                  VAT AMOUNT
                </span>
                <span className="fs-22 fw-bolder" style={{ color: "#FFCC00" }}>
                  TSh {Method.getGeneralizedAmount(totalReturnedVAT)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table Section */}
      <Card className="border border-r10px mt-6">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
              <thead>
                {/* Visual Header Grouping */}
                <tr className="fs-13 fw-bolder text-uppercase bg-light text-gray-700 border-bottom border-gray-200">
                  <th className="min-w-250px p-4 border-end">
                    Product Summary
                  </th>
                  <th
                    colSpan={2}
                    className="text-center p-4 bg-light-primary  border-opacity-25"
                  >
                    Supply Quantities
                  </th>
                  <th
                    colSpan={3}
                    className="text-center p-4 bg-light-info  border-opacity-25"
                  >
                    Unit Pricing
                  </th>

                  <th
                    colSpan={3}
                    className="text-center p-4 border-end text-gray-700"
                    style={{
                      backgroundColor: "#FFFBE6",
                      color: "#FFCC00",
                      borderColor: "rgba(255, 204, 0, 0.25)",
                    }}
                  >
                    Returned Totals
                  </th>
                </tr>
                <tr className="fs-12 fw-bold h-50px align-middle text-muted bg-light bg-opacity-50">
                  <th className="min-w-250px border-end px-4">
                    PRODUCT NAME & ORDER ID - CUSTOMER NAME
                  </th>
                  <th className="min-w-100px text-center border-start border-primary border-opacity-25">
                    OUTWARD QTY
                  </th>
                  <th className="min-w-100px text-center border-end border-info border-opacity-25 ">
                    RETURNED QTY
                  </th>

                  <th className="min-w-125px text-end">INCL. VAT</th>
                  <th className="min-w-125px text-end">EXCL. VAT</th>
                  <th className="min-w-125px text-end border-end border-info border-opacity-25">
                    VAT / UNIT
                  </th>
                  <th className="min-w-125px text-end">INCL. VAT</th>
                  <th className="min-w-125px text-end">EXCL. VAT</th>
                  <th className="min-w-125px text-end border-end border-info border-opacity-25">
                    VAT / UNIT
                  </th>
                </tr>
              </thead>
              <tbody className="fs-14 fw-600">
                {fetchLoading ? (
                  <tr>
                    <td colSpan={16}>
                      <div className="d-flex justify-content-center py-5">
                        <Loader loading={fetchLoading} />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {paymentData?.length ? (
                      paymentData.map((val: any, index: number) => {
                        const uom =
                          val?.quantityType === CartonWithDozens ||
                          val?.quantityType === CartonWithPieces
                            ? "C"
                            : val?.quantityType === Dozen
                            ? "D"
                            : val?.quantityType === Piece
                            ? "P"
                            : "";
                        const totalQty = val?.totalQuantities || 0;
                        const returnedQty = val?.totalReturnedQuantities || 0;
                        const deliveredQty =
                          val?.totalReturnedQuantities && val?.totalQuantities
                            ? val?.totalQuantities -
                              val?.totalReturnedQuantities
                            : val?.totalQuantities
                            ? val?.totalDelivered
                            : 0;

                        const rowNetInclVAT =
                          (val?.totalAmount || 0) -
                          returnedQty * (val?.averagePrice || 0);
                        const rowNetExclVAT =
                          (val?.totalTaxAmount || 0) -
                          returnedQty * (val?.averageTaxPrice || 0);
                        const rowNetVAT =
                          (val?.tax || 0) -
                          returnedQty * (val?.taxPerUnit || 0);
                        const isPositive = rowNetInclVAT >= 0;

                        return (
                          <tr
                            key={index}
                            className="align-middle border-bottom border-gray-200 hover-bg-light transition"
                          >
                            {/* Product info */}
                            <td className="border-end px-4 py-3">
                              <div className="d-flex align-items-center">
                                <div className="symbol symbol-45px border me-3 bg-white rounded shadow-sm">
                                  <ImageWithFallback
                                    src={val?.variant?.media[0]?.url}
                                    className="object-fit-contain p-1 rounded"
                                    alt=""
                                    noDefaultSize
                                  />
                                </div>
                                <div className="d-flex flex-column text-nowrap">
                                  <span className="fs-14 fw-bolder text-dark mb-1">
                                    {val?.variant?.title?.replace(
                                      /\s*\)\s*/g,
                                      ")"
                                    )}
                                  </span>
                                  <span className="fs-12 fw-600 text-muted">
                                    #{val?.refKey} - {val?.customerName}
                                  </span>
                                  <span className="fs-12 fw-500 text-muted mt-1">
                                    <i
                                      className="bi bi-truck me-1"
                                      aria-hidden="true"
                                      style={{ color: "#009EF7" }}
                                    />
                                    <span className="visually-hidden">
                                      Driver:
                                    </span>
                                    {val?.driverName || "N/A"}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* Quantities */}
                            <td className="text-center font-monospace fw-600 border-start border-primary border-opacity-10 bg-light-primary bg-opacity-25">
                              {totalQty} <span className=" fs-12">{uom}</span>
                            </td>
                            <td className="text-center font-monospace fw-bolder border-end border-info border-opacity-10 text-nowrap text-danger bg-light-primary bg-opacity-25">
                              {returnedQty}{" "}
                              <span className=" fs-12">{uom}</span>
                            </td>

                            {/* Unit Pricing */}
                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(val?.averagePrice)}
                            </td>
                            <td className="text-end fw-600 pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(
                                val?.averageTaxPrice
                              )}
                            </td>
                            <td className="text-end fw-600 pe-3 border-end border-info border-opacity-10 text-nowrap bg-light-info bg-opacity-25">
                              <span className="text-info fs-11 me-1 opacity-75">
                                TSh{" "}
                                {Method.getGeneralizedAmount(val?.taxPerUnit)}
                              </span>
                            </td>

                            {/* Returned Totals */}
                            <td className="text-end fw-bolder text-dark pe-3 text-nowrap border-start">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(
                                returnedQty * (val?.averagePrice || 0)
                              )}
                            </td>
                            <td className="text-end fw-bolder text-dark pe-3 text-nowrap">
                              <span className="text-muted fs-11 me-1 fw-600 opacity-50">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(
                                returnedQty * (val?.averageTaxPrice || 0)
                              )}
                            </td>
                            <td
                              className="text-end fw-bolder pe-4 border-end text-nowrap"
                              style={{ color: "#FFCC00" }}
                            >
                              <span className="fs-11 me-1 fw-600 opacity-75">
                                TSh
                              </span>
                              {Method.getGeneralizedAmount(
                                returnedQty * (val?.taxPerUnit || 0)
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={16}>
                          <div className="w-100 fs-15 py-8 fw-bold d-flex justify-content-center text-muted">
                            No Data found
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default FinanceReturnReport;
